import { Brand, Company, StyleEnum } from '@akiba/models_beta_temp';
import { Injectable } from '@angular/core';
import tinyColor from 'tinycolor2';
import { IColor } from '../misc/interfaces';

@Injectable({
  providedIn: 'any'
})
export class AkibaThemeService {

  defaultPrimary = '#1565c0';
  defaultAccent = '#1565c0';
  defaultWarn = '#ff0000';

  primaryColor = this.defaultPrimary;
  accentColor = this.defaultAccent;
  warnColor = this.defaultWarn;

  constructor() {
    this.saveColor(StyleEnum.Primary, this.primaryColor);
    this.saveColor(StyleEnum.Accent, this.accentColor);
    this.saveColor(StyleEnum.Warn, this.warnColor);
    this.saveTextColor(StyleEnum.PrimaryText, this.primaryColor);
  }

  setColorProperty(key: string, value: string): void {
    document.documentElement.style.setProperty(key, value);
  }

  saveColor(prefix: StyleEnum, colorCode: string): void {
    const primaryColorPalette = this.computeColors(colorCode);
    for (const color of primaryColorPalette) {
      const key1 = `--theme-${prefix}-${color.name}`;
      const value1 = color.hex;
      const key2 = `--theme-${prefix}-contrast-${color.name}`;
      const value2 = this.readableColor(value1, '#ffffff');
      this.setColorProperty(key1, value1);
      this.setColorProperty(key2, value2);
    }

    const primaryColorAlphaPalette = this.computeColorsAlpha(colorCode);
    for (const color of primaryColorAlphaPalette) {
      const key1 = `--theme-${prefix}-alpha-${color.name}`;
      const value1 = color.alpha;
      this.setColorProperty(key1, value1);
    }

  }

  saveTextColor(prefix: StyleEnum, colorCode: string): void {

    const finalColor = this.readableColor('#ffffff', colorCode);

    const primaryColorPalette = this.computeColors(finalColor);
    for (const color of primaryColorPalette) {
      const key1 = `--theme-${prefix}-${color.name}`;
      const value1 = color.hex;
      const key2 = `--theme-${prefix}-contrast-${color.name}`;
      const value2 = color.darkContrast ? 'rgba(black, 0.87)' : 'white';
      this.setColorProperty(key1, value1);
      this.setColorProperty(key2, value2);
    }
  }

  getColorObject(value: any, colorName: any): IColor {
    const c = new tinyColor(value);
    return {
      name: colorName,
      hex: c.toHexString(),
      alpha: c.toRgbString(),
      darkContrast: c.isLight()
    };
  }

  computeColors(hex: string): IColor[] {
    return [
      this.getColorObject(new tinyColor(hex).lighten(52), '50'),
      this.getColorObject(new tinyColor(hex).lighten(37), '100'),
      this.getColorObject(new tinyColor(hex).lighten(26), '200'),
      this.getColorObject(new tinyColor(hex).lighten(12), '300'),
      this.getColorObject(new tinyColor(hex).lighten(6), '400'),
      this.getColorObject(new tinyColor(hex), '500'),
      this.getColorObject(new tinyColor(hex).darken(6), '600'),
      this.getColorObject(new tinyColor(hex).darken(12), '700'),
      this.getColorObject(new tinyColor(hex).darken(18), '800'),
      this.getColorObject(new tinyColor(hex).darken(24), '900'),
      this.getColorObject(new tinyColor(hex).lighten(50).saturate(30), 'A100'),
      this.getColorObject(new tinyColor(hex).lighten(30).saturate(30), 'A200'),
      this.getColorObject(new tinyColor(hex).lighten(10).saturate(15), 'A400'),
      this.getColorObject(new tinyColor(hex).lighten(5).saturate(5), 'A700')
    ];
  }

  computeColorsAlpha(hex: string): IColor[] {
    return [
      this.getColorObject(new tinyColor(hex).setAlpha(.01), '50'),
      this.getColorObject(new tinyColor(hex).setAlpha(.10), '100'),
      this.getColorObject(new tinyColor(hex).setAlpha(.20), '200'),
      this.getColorObject(new tinyColor(hex).setAlpha(.30), '300'),
      this.getColorObject(new tinyColor(hex).setAlpha(.40), '400'),
      this.getColorObject(new tinyColor(hex).setAlpha(.50), '500'),
      this.getColorObject(new tinyColor(hex).setAlpha(.60), '600'),
      this.getColorObject(new tinyColor(hex).setAlpha(.70), '700'),
      this.getColorObject(new tinyColor(hex).setAlpha(.80), '800'),
      this.getColorObject(new tinyColor(hex).setAlpha(.90), '900'),
      this.getColorObject(new tinyColor(hex).setAlpha(.05), 'A100'),
      this.getColorObject(new tinyColor(hex).setAlpha(.25), 'A200'),
      this.getColorObject(new tinyColor(hex).setAlpha(.45), 'A400'),
      this.getColorObject(new tinyColor(hex).setAlpha(.75), 'A700')
    ];
  }

  readableColor(backGroundColor: string, foregroundColor: string): string {

    let finalColor = foregroundColor;
    let amount = 1;

    while (tinyColor.isReadable(backGroundColor, finalColor, { level: 'AA', size: 'small' }) === false) {
      finalColor = new tinyColor(foregroundColor).darken(amount).toHexString();
      amount++;
    }

    return finalColor;

  }

  changeBrand(brand?: Brand): void {

    if (brand) {

      if (brand.primary) {
        this.primaryColor = brand.primary;
        this.saveColor(StyleEnum.Primary, brand.primary);
        this.saveTextColor(StyleEnum.PrimaryText, brand.primary);
      }

      if (brand.accent) {
        this.accentColor = brand.accent;
        this.saveColor(StyleEnum.Accent, brand.accent);
      }

      if (brand.accent) {
        this.accentColor = brand.accent;
        this.saveColor(StyleEnum.Warn, brand.warn);
      }

    } else {

      const akibaBrand = new Brand();
      this.primaryColor = akibaBrand.primary;
      this.accentColor = akibaBrand.accent;
      this.saveColor(StyleEnum.Primary, akibaBrand.primary);
      this.saveColor(StyleEnum.Accent, akibaBrand.accent);
      this.saveColor(StyleEnum.Warn, akibaBrand.warn);
      this.saveTextColor(StyleEnum.PrimaryText, akibaBrand.primary);

    }

  }

  changeTheme(aCompany?: Company): void {

    if (aCompany) {

      if (aCompany.brand.primary) {
        this.primaryColor = aCompany.brand.primary;
        this.saveColor(StyleEnum.Primary, aCompany.brand.primary);
        this.saveTextColor(StyleEnum.PrimaryText, aCompany.brand.primary);
      }

      if (aCompany.brand.accent) {
        this.accentColor = aCompany.brand.accent;
        this.saveColor(StyleEnum.Accent, aCompany.brand.accent);
      }

      if (aCompany.brand.accent) {
        this.accentColor = aCompany.brand.accent;
        this.saveColor(StyleEnum.Warn, aCompany.brand.warn);
      }

    } else {

      const akibaBrand = new Brand();
      this.primaryColor = akibaBrand.primary;
      this.accentColor = akibaBrand.accent;
      this.saveColor(StyleEnum.Primary, akibaBrand.primary);
      this.saveColor(StyleEnum.Accent, akibaBrand.accent);
      this.saveColor(StyleEnum.Warn, akibaBrand.warn);
      this.saveTextColor(StyleEnum.PrimaryText, akibaBrand.primary);

    }

  }

  alphaFromHEX(hex: string, alphaValue: number): string {
    return new tinyColor(hex).setAlpha(alphaValue).toRgbString();
  }

  resetThemeColor() {

  }

}
